import { Component, OnInit, ViewChild, ElementRef } from "@angular/core";
import { SearchService } from "../../../services/search.service";
import { DataService } from "../../../services/data.service";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastService } from "../../../components/toast/toast.service";
import { DataGroupColumn } from "../../../models/data-group.model";
import { DataType } from "../../../models/data-type.model";
import { LoadingSpinnerService } from "../../../components/loading-spinner/loading-spinner.service";
import { Parameter } from "../search.componentOLD";
import { FamilyStatus, Patient } from "../../../models/patient.model";
import * as _ from "underscore";
import * as moment from "moment";
import { DownloadService } from "../../../services/download.service";
import { TagListComponent } from "../../../components/tag-list/tag-list.component";
import { MetaDataMapping, MetaData } from "../../../models/meta-data.model";
import { BatchDownloadModalComponent } from "../../../components/modal/modals/batch-download-modal.component";
import { LowyGridComponent } from "../../../components/grid/grid.component";
import { SearchStatus } from "../../../models/search.model";
import Utils from "../../../services/utils";
import { BuildSearchDataHelper } from "./build-search-data.helper";
import { TagService } from "../../../services/tag.service";

@Component({
  templateUrl: "./search-results.component.html",
  styleUrls: ["./search-results.component.scss"],
  providers: [DataService, DownloadService, TagService],
})
export class SearchResultsComponent implements OnInit {
  public showAllData = false;

  resultGroups: Array<{
    dataType: string;
    count?: number;
    name: string;
  }>;
  gridDataType: string;
  searchResults: any;

  private parameters: Parameter[];
  private showOnlyMatching = false;

  @ViewChild("grid") grid: LowyGridComponent;
  @ViewChild("downloadUrlLink") downloadUrlLink: ElementRef;

  @ViewChild("tagList") tagList: TagListComponent;

  private buildSearch: BuildSearchDataHelper;

  constructor(
    private searchService: SearchService,
    private dataService: DataService,
    private route: ActivatedRoute,
    private router: Router,
    private toastService: ToastService,
    private downloadService: DownloadService,
    private tagService: TagService,
    private loadingSpinnerService: LoadingSpinnerService
  ) {
    this.buildSearch = new BuildSearchDataHelper();
  }

  async ngOnInit() {
    this.loadingSpinnerService.start();
    this.gridDataType = "patient";

    try {
      // are there cached results we can use?
      if (this.searchService.isRecentSearchResults()) {
        this.searchResults = this.searchService.getRecentSearchResults();
      } else {
        // the search runs in the background and lets us know when it's done (to avoid timeout for single call)
        let searchStatus = await this.searchService.executeSearch();

        let searchTimer = 100;
        while (searchStatus.status === SearchStatus.pending) {
          searchStatus = await this.searchService.getSearchStatus(searchStatus);
          await Utils.sleep(searchTimer);
          if (searchTimer < 10000) {
            searchTimer += searchTimer;
          }
        }
        if (searchStatus.status === SearchStatus.error) {
          throw searchStatus.errorMessage;
        }

        const rawSearchResults = await this.searchService.getSearchResults(
          searchStatus
        );
        const patientsForResults = [];

        // relocated here from server side, break up patients by metaData
        // each record in patient.metaDatas implies a unique visit or eye, split out in separate patient records for UI
        for (let i = 0; i < rawSearchResults["patient"].length; i++) {
          const patient = rawSearchResults["patient"][i];
          if (!patient.metaDatas || patient.metaDatas.length === 0) {
            patientsForResults.push(patient);
          } else {
            for (let j = 0; j < patient.metaDatas.length; j++) {
              const newPatient = JSON.parse(JSON.stringify(patient));
              const metaData = newPatient.metaDatas[j];
              newPatient.metaData = metaData;
              newPatient.metaData.metaDataMapping =
                metaData.metaDataMappings[0];
              patientsForResults.push(newPatient);
            }
          }
        }
        rawSearchResults["patient"] = patientsForResults;
        this.searchResults = rawSearchResults;
        this.searchService.saveSearchResults(this.searchResults);
      }

      const patientData = await this.searchService.patientData;
      const metaData = await this.searchService.metaData;
      this.parameters = patientData;
      const datatypes = await this.dataService.getDataTypes();
      this.buildDataTypes(datatypes);
      this.viewResults(this.gridDataType);
      this.updateTagList();
      this.loadingSpinnerService.stop();
    } catch (err) {
      this.loadingSpinnerService.stop();
      this.toastService.throwToast(err.message);
      this.router.navigate(["/search"]);
    }
  }

  // just for header display
  private updateTagList() {
    this.tagList.setFilters({
      patientData: this.searchService.patientData as any,
      imageData: this.searchService.metaData as any,
      dataTypes: null,
      tags: this.searchService.searchTags as any,
    });
  }

  async buildDataTypes(dataTypeFilters: Array<DataType>) {
    // first check if no filters of a specific type, populate based on all attached data items (instead of only ones that match filters)
    const dataTypeFilterCount = {};
    const secondaryDataTypes = ["image", "genetic", "metabolomic"];
    secondaryDataTypes.forEach((secondaryDataType) => {
      dataTypeFilterCount[secondaryDataType] = 0;
    });

    for (let i = 0; i < this.searchResults["patient"].length; i++) {
      secondaryDataTypes.forEach((secondaryDataType) => {
        if (
          dataTypeFilterCount[secondaryDataType] === 0 &&
          this.searchResults["patient"][i] &&
          this.searchResults["patient"][i].data &&
          this.searchResults["patient"][i].data[secondaryDataType]
        ) {
          // safe checks
          // since we have no filters are the current dataType, populate with ALL of that type (see jira LOWY-618)
          this.searchResults[secondaryDataType] = this.searchResults[
            secondaryDataType
          ].concat(this.searchResults["patient"][i].data[secondaryDataType]);
        }
      });
    }

    // now just fill in everything
    this.resultGroups = [];
    let totalCount = 0;

    dataTypeFilters.forEach((dataType) => {
      if (this.searchResults[dataType.abbr]) {
        this.resultGroups.push({
          dataType: dataType.abbr,
          name: dataType.name,
          count: this.searchResults[dataType.abbr].length,
        });
        totalCount += this.searchResults[dataType.abbr].length;
      }
    });

    this.resultGroups.unshift({
      dataType: "summary",
      name: "Summary",
      // count: totalCount
      count: null,
    });
  }

  async viewResults(dataType: string) {
    this.gridDataType = dataType;
    let columns: Array<any>;
    let columnHeaders: Array<string>;
    let data: Array<any>;

    // build columns
    switch (dataType) {
      case "summary":
        columns = [
          "checkbox",
          "dataType",
          "patientId",
          "images",
          "snp",
          "wes",
          "wgs",
          "metabolomics",
          "tags",
        ];
        columnHeaders = [
          "checkbox",
          "dataType",
          "Patient ID",
          "Images",
          "SNP",
          "WES",
          "WGS",
          "Metabolomics",
          "Patient Tags",
        ];
        const patientIds = _.uniq(
          _.map(this.searchResults.patient, "patientId")
        );
        const patientTags = _.groupBy(
          await this.tagService.getTagsForPatients(patientIds),
          "dataId"
        );
        data = this.buildSearch.buildSummaryData(
          this.searchResults,
          patientTags
        );
        break;
      case "image":
        columns = [
          "checkbox",
          "dataType",
          "patientId",
          "fileName",
          "imageType",
          "modality",
          "studyId",
          "visitId",
          "imageDate",
        ];
        columnHeaders = [
          "checkbox",
          "dataType",
          "Patient ID",
          "File name",
          "Image Type",
          "Modality",
          "Study ID",
          "Visit ID",
          "Image Date",
        ];
        let imageSearchResults;
        if (this.showOnlyMatching) {
          imageSearchResults = _.filter(
            this.searchResults.image,
            (image) => image["dataMatchesFilters"] === true
          );
        } else {
          imageSearchResults = this.searchResults.image;
        }
        data = this.buildImageData(imageSearchResults);
        break;
      case "patient":
        columns = [
          { columnId: "checkbox" },
          { columnId: "dataType" },
          { columnId: "patientId" },
          { columnId: "patId2" },
          { columnId: "patId3" },
          { columnId: "patId4" },
        ];
        columnHeaders = [
          "checkbox",
          "dataType",
          "Patient ID",
          "A Number",
          "Site ID",
          "ID 4",
        ];
        for (let i = 0; i < this.parameters.length; i++) {
          columns.push({
            columnId: this.parameters[i].columnId,
            spreadsheetId: this.parameters[i].spreadsheetId,
          });
          columnHeaders.push(
            this.parameters[i].spreadsheetId +
              ": " +
              this.parameters[i].columnName
          );
        }
        const dataset = this.buildPatientData(
          this.searchResults.patient,
          columns,
          columnHeaders
        );
        data = dataset.data;
        columns = dataset.columns; // this is because buildPatientData re-arranges order of columns
        columnHeaders = dataset.columnHeaders;
        // rip out spreadsheetId from columns here
        const newCols = [];
        for (const col of columns) {
          newCols.push(col.columnId);
        }
        columns = newCols;
        break;
      case "metabolomic":
        columns = [
          "checkbox",
          "dataType",
          "patientId",
          "fileName",
          "fileType",
          "visitId",
          "createdDate",
        ];
        columnHeaders = [
          "checkbox",
          "dataType",
          "Patient ID",
          "File Name",
          "File Type",
          "Visit ID",
          "Upload Date",
        ];
        let metabolomicSearchResults;
        if (this.showOnlyMatching) {
          metabolomicSearchResults = _.filter(
            this.searchResults.metabolomic,
            (metabolomic) => metabolomic["dataMatchesFilters"] === true
          );
        } else {
          metabolomicSearchResults = this.searchResults.metabolomic;
        }
        data = this.buildMetabolomicData(metabolomicSearchResults);
        break;
      case "genetic":
        columns = [
          "checkbox",
          "dataType",
          "patientId",
          "batch",
          "altId",
          "fileName",
          "fileType",
          "createdDate",
        ];
        columnHeaders = [
          "checkbox",
          "dataType",
          "Patient ID",
          "Batch",
          "Alt ID",
          "File Name",
          "File Type",
          "Upload Date",
        ];
        let geneticSearchResults;
        if (this.showOnlyMatching) {
          geneticSearchResults = _.filter(
            this.searchResults.genetic,
            (genetic) => genetic["dataMatchesFilters"] === true
          );
        } else {
          geneticSearchResults = this.searchResults.genetic;
        }
        data = this.buildGeneticData(geneticSearchResults);
        break;
      default:
        break;
    }

    this.grid.dataReady(data, columns, columnHeaders);
  }

  private buildImageData(images: Array<any>): Array<any> {
    const imageData = [];
    for (const image of images) {
      let imageType, imagePlatform, studyId, modality, imageDate;
      for (const meta of image.metaDatas) {
        if (meta.metaDataMappings[0].columnId === "IMAGE_TYPE") {
          imageType = meta.value;
        } else if (meta.metaDataMappings[0].columnId === "IMAGE_PLATFORM") {
          imagePlatform = meta.value;
        } else if (meta.metaDataMappings[0].columnId === "STUDY_ID") {
          studyId = meta.value;
        } else if (meta.metaDataMappings[0].columnId === "SERIESCLEAN") {
          modality = meta.value;
        } else if (meta.metaDataMappings[0].columnId === "PHOTO_DATE") {
          imageDate = meta.value;
        }
      }
      imageData.push({
        _id: image.imageId,
        checkbox: "checkbox",
        dataType: "image",
        iconType: image.viewableImage ? "viewableImage" : "image",
        patientId: image.patientId,
        fileName: image.name,
        imageType: imageType,
        imagePlatform: imagePlatform,
        studyId: studyId,
        visitId: image.visitId,
        modality: modality,
        imageDate: imageDate,
      });
    }
    return imageData;
  }
  private buildMetabolomicData(metabolomics: Array<any>): Array<any> {
    const metabolomicData = [];
    for (const metabolomic of metabolomics) {
      metabolomicData.push({
        _id: metabolomic.metabolomicId,
        checkbox: "checkbox",
        dataType: "metabolomic",
        iconType: "metabolomic",
        patientId: metabolomic.patientId,
        fileName: metabolomic.name,
        fileType: metabolomic.meta["FILETYPE"],
        visitId: metabolomic.visitId,
        createdDate: moment(metabolomic.createdAt).format("MMM DD, YYYY"),
      });
    }
    return metabolomicData;
  }
  private buildGeneticData(genetics: Array<any>): Array<any> {
    const geneticData = [];
    for (const genetic of genetics) {
      let fileType, altId, batch;
      for (const meta of genetic.metaDatas) {
        if (meta.metaDataMappings[0].columnId === "FILETYPE") {
          fileType = meta.value;
        } else if (meta.metaDataMappings[0].columnId === "ALT_ID") {
          altId = meta.value;
        } else if (meta.metaDataMappings[0].columnId === "BATCH") {
          batch = meta.value;
        }
      }
      geneticData.push({
        _id: genetic.geneticId,
        checkbox: "checkbox",
        dataType: "genetic",
        iconType: "genetic",
        patientId: genetic.patientId,
        fileName: genetic.name,
        fileType: fileType,
        visitId: genetic.visitId,
        altId: altId,
        batch: batch,
        createdDate: moment(genetic.createdAt).format("MMM DD, YYYY"),
      });
    }
    return geneticData;
  }

  private buildPatientData(
    patients: Array<any>,
    columns: Array<DataGroupColumn>,
    columnHeaders: Array<string>
  ): {
    data: Array<any>;
    columns: Array<DataGroupColumn>;
    columnHeaders: Array<string>;
  } {
    const patientData: Patient[] = [];
    const dataColumnType = {}; // mirror indices of data object
    let hasvisit = false;
    let haseye = false;

    for (const patient of patients) {
      if (patient) {
        const data: Patient = {} as Patient;
        data["_id"] = patient.patientId;
        data["hasFamily"] = patient.familyId != null;
        for (const column of columns) {
          // account for static types
          if (column.columnId === "checkbox") {
            data[column.columnId] = "checkbox";
          } else if (column.columnId === "dataType") {
            // for display icon, if has familyId
            data[column.columnId] = "patient";
            if (
              [
                FamilyStatus.familyMember,
                FamilyStatus.probandWithFamily,
              ].includes(patient.familyStatus)
            ) {
              data["iconType"] = "family";
            } else {
              data["iconType"] = "patient";
            }
          } else if (column.columnId === "patientId") {
            data[column.columnId] = patient.patientId;
          } else if (column.columnId === "patId2") {
            data[column.columnId] = patient.patId2;
          } else if (column.columnId === "patId3") {
            data[column.columnId] = patient.patId3;
          } else if (column.columnId === "patId4") {
            data[column.columnId] = patient.patId4;
          } else {
            if (
              patient.metaData &&
              patient.metaData.metaDataMapping &&
              column.columnId === patient.metaData.metaDataMapping.columnId
            ) {
              data[column.columnId] = patient.metaData.value;
              data["columnId"] = column.columnId;
              if (
                patient.metaData.visit &&
                patient.metaData.visit !== "VISITLESS"
              ) {
                data["visitId"] = patient.metaData.visit;
                hasvisit = true;
                dataColumnType[column.columnId] = "hasvisit";
              } else if (
                patient.metaData.visit &&
                patient.metaData.visit === "VISITLESS"
              ) {
                data["visitId"] = "VISITLESS";
              }
              if (patient.metaData.eye) {
                data["eye"] = patient.metaData.eye;
                haseye = true;
                dataColumnType[column.columnId] = "haseye";
              }
            }
          }
        }
        patientData.push(data);
      }
    }

    // reorg columns to move visitSpecificData to the end
    // if there were any, add Visit column and its visit
    if (hasvisit) {
      const columnsToAppend = [];
      const columnHeadersToAppend = [];
      for (let i = 0; i < columns.length; i++) {
        if (dataColumnType[columns[i].columnId] === "hasvisit") {
          columnsToAppend.push(columns[i]);
          columnHeadersToAppend.push(columnHeaders[i]);
          columns.splice(i, 1);
          columnHeaders.splice(i, 1);
          i--;
        }
      }
      columns.push({ columnId: "visitId", columnName: "Visit" });
      columnHeaders.push("Visit");
      for (const column of columnsToAppend) {
        columns.push(column);
      }
      for (const columnHeader of columnHeadersToAppend) {
        columnHeaders.push(columnHeader);
      }
    }

    // and do the exact same for eye
    if (haseye) {
      const columnsToAppend = [];
      const columnHeadersToAppend = [];
      for (let i = 0; i < columns.length; i++) {
        if (dataColumnType[columns[i].columnId] === "haseye") {
          columnsToAppend.push(columns[i]);
          columnHeadersToAppend.push(columnHeaders[i]);
          columns.splice(i, 1);
          columnHeaders.splice(i, 1);
          i--;
        }
      }
      columns.push({ columnId: "eye", columnName: "Eye" });
      columnHeaders.push("Eye");
      for (const column of columnsToAppend) {
        columns.push(column);
      }
      for (const columnHeader of columnHeadersToAppend) {
        columnHeaders.push(columnHeader);
      }
    }

    // we have a list of of everything and its meta data all nice and organized
    // group by patient id, then visit id, then eye, and map onto below object groupedPatientRows

    const groupedPatientRows: {
      [patientId: string]: {
        [visitId: string]: {
          [eye: string]: Patient[];
        };
      };
    } = {};

    _.chain(patientData)
      .groupBy((patient: Patient) => patient.patientId)
      .map((value, key) => {
        return { patientId: key, patients: value };
      })
      .each((groupedPatients) => {
        groupedPatientRows[groupedPatients.patientId] = {};
        _.chain(groupedPatients.patients)
          .groupBy((patient: Patient) => patient.visitId)
          .map((value, key) => {
            return { visitId: key, patients: value };
          })
          .each((groupedVisits) => {
            groupedPatientRows[groupedPatients.patientId][
              groupedVisits.visitId
            ] = {};
            _.chain(groupedVisits.patients)
              .groupBy((patient: Patient) => patient["eye"])
              .map((value, key) => {
                return { eye: key, patients: value };
              })
              .each((groupedEyes) => {
                groupedPatientRows[groupedPatients.patientId][
                  groupedVisits.visitId
                ][groupedEyes.eye] = groupedEyes.patients;
              });
          });
      });

    const finalPatients = [];
    // now we unfold it, and first map eyeless data on eyespecific, and visitless onto visitspecific
    _.each(groupedPatientRows, (groupedPatients, patientId) => {
      // at the end of this block eached groupedPatientRows will represent a pushed set of data for a single patient
      let hasVisitedData;
      let hasEyedData; // used to track if eyeless or visitless dataset (per patient)

      let visitLessForPatient = [];
      if (groupedPatients["undefined"]) {
        visitLessForPatient = groupedPatients["undefined"]["undefined"];
      }
      delete groupedPatients["undefined"];
      _.each(groupedPatients, (groupedVisits, visitId) => {
        hasVisitedData = true;
        const eyeLessForVisit = groupedVisits["undefined"];
        delete groupedVisits["undefined"];

        _.each(groupedVisits, (groupedVisit) => {
          hasEyedData = true;

          _.each(groupedVisit, (patientInVisit) => {
            Object.assign(patientInVisit, ...eyeLessForVisit); // push eyeless onto eye specific
          });
        });

        if (!hasEyedData) {
          groupedPatients[visitId]["undefined"] = eyeLessForVisit;
        }
      });

      _.each(groupedPatients, (groupedPatient) => {
        _.each(groupedPatient, (patientInOuterGroup) => {
          _.each(patientInOuterGroup, (patientInInnerGroup) => {
            Object.assign(patientInInnerGroup, ...visitLessForPatient); // push visitless onto visit specific
          });
        });
      });

      if (!hasVisitedData) {
        // all visitless, just merge and push
        const visitlessToPush = visitLessForPatient.shift(); // pop the first and merge the rest
        Object.assign(visitlessToPush, ...visitLessForPatient);
        finalPatients.push(visitlessToPush);
      } else {
        // now we're fully unfolded, trawl and push to final list
        _.each(groupedPatients, (visitPatients) => {
          _.each(visitPatients, (eyePatients) => {
            const eyeToPush = eyePatients.shift();
            Object.assign(eyeToPush, ...eyePatients); // merge data that's same visit and same eye
            finalPatients.push(eyeToPush);
          });
        });
      }
    });

    const uniqPatients = _.uniq(finalPatients, "_id");
    this.updateResultsCount("patient", uniqPatients.length);
    return {
      data: finalPatients,
      columns: columns,
      columnHeaders: columnHeaders,
    };
  }

  updateResultsCount(dataType: string, count: number) {
    for (let i = 0; i < this.resultGroups.length; i++) {
      if (this.resultGroups[i].dataType === dataType) {
        this.resultGroups[i].count = count;
      }
    }
  }

  public showAllDataChange() {
    this.showAllData = !this.showAllData;
  }
}



// WEBPACK FOOTER //
// ./src/app/home/search/results/search-results.component.ts